import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import PatientQueueItem from './PatientQueueItem';
import PatientDetails from '../patientDetails';
import PaymentModal from '../../Bills/PaymentModal';
import { baseSelectStyles } from '../../../shared/selectStyles';
import {
  billUpdated
} from '../../../../ReduxSlices/Bills/billSlice';
// Redux: patients slice
import {
  fetchPatients,
  // upsertPatient,
  selectSelectedPatient,
  selectPatients,
  setSelectedPatient,
} from '../../../../ReduxSlices/Patients/patientSlice';

// Redux: patient queue slice
import {
  fetchQueue,
  updateQueue,
  addToQueue,
  selectQueueLoading,
  selectQueue,
  updateQueueStatus,
  removeQueueEntry
} from '../../../../ReduxSlices/Patients/patientQueueSlice';

import {
  previewBill,
  clearPreview,
  selectBillPreview,
  selectShowPaymentModal
} from '../../../../ReduxSlices/Bills/billSlice';
import LoadingDots from '../../../shared/LoadingDots';
import { fetchConsultations } from '../../../../ReduxSlices/Admin/consultationServiceSlice';
import { fetchLabTestTypes } from '../../../../ReduxSlices/Admin/labTestServiceSlice';

const PatientQueueList = ({ role }) => {
  const dispatch = useDispatch();
  const [selectedServices, setSelectedServices] = useState({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [errors, setErrorMessage] = useState({});
  const [reasonForVisit, setReasonForVisit] = useState('consultation');


  // Redux state
  const patients = useSelector(selectPatients);
  const queue = useSelector(selectQueue);
  const loading = useSelector(selectQueueLoading);
  const selectedPatient = useSelector(selectSelectedPatient);
  const showPaymentModal = useSelector(selectShowPaymentModal);
  const paymentPreview = useSelector(selectBillPreview);
  const consultationServices = useSelector((state) => state.consultations.consultations);
  const labTestServices = useSelector((state) => state.labServices.labTests);


  // Local UI state
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [adding, setAdding] = useState(false);
  const selectedIdInt = parseInt(selectedPatientId, 10);


  useEffect(() => {
   if (consultationServices.length === 0) {
      dispatch(fetchConsultations());
    }

     if (labTestServices.length === 0) {
    dispatch(fetchLabTestTypes());
   
  }
}, [dispatch, consultationServices.length, labTestServices.length]);
  /**
   *  Initiate a payment preview
   * Calls API → gets bill preview → opens PaymentModal
   */
  const handleInitiatePayment = async (patientId, category, serviceId, patientQueueId, entry) => {
   setPreviewLoading(true);
    try {
          //  Capture the result of the previewBill dispatch
          const preview = await dispatch(
            previewBill({ patientId, category, serviceId, patientQueueId })
          ).unwrap();         

          // Clear previous errors if payment can proceed
        setErrorMessage(prev => {
          const newErrors = { ...prev };
          delete newErrors[entry.id];
          return newErrors;
        });

      } catch (err) {
      console.error('Failed to preview bill:', err);

      setErrorMessage(prev => ({
        ...prev,
        [entry.id]: err?.data?.error || "Failed to preview bill"
      }));

      dispatch(clearPreview());
    }
   finally {
        setPreviewLoading(false);
      } 
  };

  const handleRemoveQueueEntry = (entry) => {
    if (!entry.active) return;
  // If any payment is still unpaid, block removal
  const hasUnpaidBill = 
    (entry.lab_billable) || 
    (entry.has_unpaid_medications);

  if (hasUnpaidBill) {
       setErrorMessage(prev => ({
        ...prev,
        [entry.id]: "Please pay pending bills."
       }));
    return;
  }

    //  OPTIMISTIC REMOVE
  dispatch(updateQueue({
    actionType: 'update',
    entry: { id: entry.id, isRemoving: true, active: false }
  }));


  //Remove error when removal is allowed
  setErrorMessage(prev => {
    const newErrors ={ ...prev};
    delete newErrors[entry.id];
    return newErrors;
  });

  // Safe to remove
  dispatch(removeQueueEntry(entry.id));
};


  const handleStatusChange = async (id, status) => {
    const prevEntry = queue.find((e) => e.id === id);
    dispatch(updateQueue({ actionType: 'update', entry: { id, status } }));

    try {
      await dispatch(updateQueueStatus({ id, status })).unwrap();
    } catch (err) {
      console.error("Status update failed:", err);
      // rollback
      dispatch(updateQueue({ actionType: 'update', entry: { id, status: prevEntry.status } }));
    }
  };


  /**
   * Helper: check if patient is already in queue (not done)
   */
  const isInQueue = (patientId) =>
    queue.some((entry) => entry.patient_id === patientId && entry.status !== 'done');

  /**
   * Add patient to queue
   * Prevents duplicates → POST to backend → clears selector
   */
const handleAddToQueue = async () => {
  if (!selectedPatientId) return;
  setAdding(true);
  try {
    await dispatch(addToQueue({
    patientId: selectedPatientId,
    intent: reasonForVisit,
  })).unwrap();
    setSelectedPatientId('');
    setReasonForVisit('consultation');
  } catch (err) {
    console.error('Failed to add to queue:', err);
  } finally {
    setAdding(false);
  }
};

  
useEffect(() => {
  if (patients.length === 0) {
    dispatch(fetchPatients());
  }

  if (queue.length === 0) {
    dispatch(fetchQueue());
  }
}, [dispatch, patients.length, queue.length]);

 /**
   *  If a patient is selected, show PatientDetails 
   */
  if (selectedPatient) {
    return <PatientDetails patient={selectedPatient} role={role} />;
  }

  return (
    <div className="queue p-4 bg-white rounded shadow mt-8">
      <h2 className="text-xl font-bold mb-4">Patient Queue</h2>

      {/*  Patient Selector + Add to Queue */}
     {selectedPatientId && isInQueue(selectedIdInt) && (
      <p className="text-red-500 text-sm">This patient is already in the queue</p>
    )}

      <div className="select-patient mb-4 flex gap-2 items-center">

      <Select
          value={patients
          .map((p) => ({ label: p.name, value: p.id }))
          .find((option) => option.value === parseInt(selectedPatientId)) || null}
          options={patients.map((p) => ({ id: p.id, label: p.name, value: p.id }))}
          onChange={(option) => setSelectedPatientId(option?.value || '')}
          placeholder="Search patient..."
          isSearchable
          className="w-[250px]"
          styles={baseSelectStyles}
        />

        <Select
            value={{
              label:
                reasonForVisit === 'consultation'
                  ? 'Consultation'
                  : reasonForVisit === 'lab'
                  ? 'Lab Only'
                  //  : reasonForVisit === 'medication'
                  // ? 'Medication Only'
                  : '',
              value: reasonForVisit,        
            }}
          placeholder="Select service"

            options={[
              { label: 'Consultation', value: 'consultation' },
              { label: 'Lab Only', value: 'lab' },
              // { label: 'Medication Only', value: 'medication' },
            ]}
            onChange={(option) => setReasonForVisit(option.value)}
            className="w-[220px]"
            isSearchable={false}
            styles={baseSelectStyles}
          />

        <button
          onClick={handleAddToQueue}
          type="button"
          className="add-to-queue bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
          disabled={!selectedPatientId || isInQueue(selectedIdInt) || adding}
        >
          <svg
            className="plus-icon"
            fill="#38AD3E"
            width="20"
            height="15"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M13 11h8a1 1 0 1 1 0 2h-8v8a1 1 0 1 1-2 0v-8H3a1 1 0 1 1 0-2h8V3a1 1 0 1 1 2 0v8z" />
          </svg>
           {adding ? "Adding..." : "Add to Queue"}
        </button>
      </div>

      {loading && <LoadingDots height={10} width={30} color="#38AD3E" /> }

      {!loading && queue.length === 0 && <p>No patients in the queue.</p>}

      {!loading && queue.length > 0 && (
        <ul className="queue-items divide-y">
          { queue.map((entry, index) => {

            return (
              <PatientQueueItem
                key={entry.id}
                entry={entry}
                position={index + 1}
                // Queue actions
                onStartVisit={(id) => handleStatusChange(id, 'being_seen')}
                onMarkDone={(id) => handleStatusChange(id, 'done')}
                onMarkWaitingLab={(id) => handleStatusChange(id, 'waiting_for_lab_results')}
                onRemove={() => handleRemoveQueueEntry(entry)}
                // Patient selection
                onClick={() => dispatch(setSelectedPatient({...entry.patient, patientQueueId: entry.id }))}
                // Payments
                onPay={(category, serviceId) =>{

                  if(category === 'lab') {
                    serviceId = serviceId || ''
                  }
                  
                    console.log("🚀 onPay CALLED:", {
                    category,
                    serviceId,
                    entryId: entry.id,
                  });

              if ((category === 'consultation' ) && !serviceId) return;                
                       
                
              handleInitiatePayment(entry.patient_id, category, serviceId, entry.id, entry)
                }}
                onServiceChange = {(entryId, serviceId,category) => {
                  setSelectedServices(prev => ({
                    ...prev,
                    [entryId] : {
                    ...prev[entryId],
                    [category]: serviceId ? parseInt(serviceId) : null
                  }
                }));
                }}
                selectedConsultation={selectedServices[entry.id]?.consultation || ''}
                selectedLab={selectedServices[entry.id]?.lab || ''}
                consultationServices={consultationServices}
                labTestServices={labTestServices}
                labBillable={entry.lab_billable}
                role={role}
                errorMessage={errors[entry.id]}
              />
            );
          })}
        </ul>
      )}

      {/* Payment Modal */}
      { previewLoading ? (
        <div className='overlay-loading'>
         <LoadingDots height={20} width={50} color="#38AD3E" />
         </div>
        ) :
      showPaymentModal && paymentPreview ? (
        <PaymentModal
          previewData={paymentPreview}
          onClose={() => {
           dispatch(clearPreview());
          }}
          onPaymentSuccess={() => {
            dispatch(billUpdated({ ...paymentPreview.bill, status: 'paid' }));
            // Clear selected services after successful payment
            setSelectedServices(prev => {
              const copy = { ...prev };
              delete copy[paymentPreview.patient_queue_id];
              return copy;
            });
            dispatch(clearPreview());
          }}        
                  />
      ) : null }
    </div>
  );
};

export default PatientQueueList;
